
import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import {
    FaHeart,
    FaRegHeart,
    FaMapMarkerAlt,
    FaHotel,
    FaSearch,
    FaStar,
    FaArrowRight,
    FaShieldAlt,
    FaWifi,
    FaSwimmingPool,
    FaUtensils,
    FaRedo,
} from "react-icons/fa";

export default function HotelSearch() {
    const [hotels, setHotels] = useState([]);
    const location = useLocation();
    const searchData = location.state;
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [wishlist, setWishlist] = useState([]);

    /* =====================================================
       WISHLIST
    ===================================================== */

    useEffect(() => {
        const storedWishlist =
            JSON.parse(localStorage.getItem("wishlist")) || [];

        setWishlist(storedWishlist);
    }, []);

    const updateWishlist = (updated) => {
        setWishlist(updated);
        localStorage.setItem("wishlist", JSON.stringify(updated));
    };

    const toggleWishlist = (hotel) => {
        const exists = wishlist.find(
            (item) => item.id === hotel.id
        );

        const updated = exists
            ? wishlist.filter((item) => item.id !== hotel.id)
            : [...wishlist, hotel];

        updateWishlist(updated);
    };

    const isWishlisted = (id) => {
        return wishlist.some((item) => item.id === id);
    };

    /* =====================================================
       FETCH HOTELS
    ===================================================== */

    useEffect(() => {
        const fetchHotels = async () => {
            setLoading(true);
            setError(false);

            try {
                const res = await axios.get(
                    "https://cheapdubaihotels.com/api/hotels",
                    {
                        params: {
                            destination: searchData?.destination,
                        },
                    }
                );

                setHotels(res.data);
            } catch (err) {
                console.error(err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchHotels();
    }, [searchData]);

    /* =====================================================
       HOTEL NAVIGATION
    ===================================================== */

    const openHotel = (slug) => {
        navigate(`/hotels/${slug}`);
    };

    return (
        <div className="min-h-screen bg-[#F5F8FA]">

            {/* =====================================================
          SEARCH HERO
      ===================================================== */}

            <section className="relative overflow-hidden bg-[#073F3D]">

                {/* Decorative Background */}

                <div className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-[#0B5552]/50 blur-3xl" />

                <div className="pointer-events-none absolute -left-32 bottom-[-120px] h-80 w-80 rounded-full bg-[#8B0000]/20 blur-3xl" />

                <div className="pointer-events-none absolute right-[12%] top-1/2 hidden h-40 w-40 -translate-y-1/2 rounded-full border border-white/5 lg:block" />

                <div className="pointer-events-none absolute right-[14%] top-1/2 hidden h-24 w-24 -translate-y-1/2 rounded-full border border-[#D7A74A]/15 lg:block" />

                <div
                    className="
            relative z-10 mx-auto max-w-[1440px]
            px-5 pb-12 pt-28
            sm:px-8 sm:pb-14 sm:pt-32
            lg:px-12 lg:pb-16 lg:pt-36
            xl:px-16
          "
                >

                    <div className="max-w-3xl">

                        {/* Badge */}

                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#D7A74A] backdrop-blur-sm">
                            <FaSearch />
                            Hotel Search
                        </div>

                        {/* Heading */}

                        <h1 className="mt-5 text-3xl font-extrabold tracking-tight !text-white sm:text-4xl lg:text-5xl">
                            Find Your{" "}
                            <span className="text-[#D7A74A]">
                                Perfect Stay
                            </span>
                        </h1>

                        <div className="mt-4 h-1 w-16 rounded-full bg-[#8B0000]" />

                        {/* Description */}

                        <p className="mt-4 max-w-2xl text-sm leading-7 text-white/65 sm:text-base">
                            {searchData?.destination
                                ? `Discover comfortable hotels and great stays in ${searchData.destination}.`
                                : "Discover comfortable hotels, great locations and the best available rates."}
                        </p>

                        {/* Search Info */}

                        <div className="mt-6 flex flex-wrap items-center gap-3">

                            <div className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.07] px-4 py-3 text-xs font-semibold text-white/85 backdrop-blur-sm">
                                <FaMapMarkerAlt className="text-[#D7A74A]" />
                                {searchData?.destination || "Dubai, UAE"}
                            </div>

                            <div className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.07] px-4 py-3 text-xs font-semibold text-white/85 backdrop-blur-sm">
                                <FaHotel className="text-[#D7A74A]" />

                                {loading
                                    ? "Searching hotels..."
                                    : `${hotels.length} ${hotels.length === 1 ? "Hotel" : "Hotels"
                                    } Found`}
                            </div>

                        </div>
                    </div>
                </div>

                {/* Bottom Accent */}

                <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#8B0000] via-[#D7A74A] to-[#0B5552]" />

            </section>

            {/* =====================================================
          CONTENT
      ===================================================== */}

            <section className="relative overflow-hidden px-5 py-12 sm:px-8 sm:py-14 lg:px-12 lg:py-16 xl:px-16">

                {/* Background Decorations */}

                <div className="pointer-events-none absolute -left-40 top-0 h-96 w-96 rounded-full bg-[#0B5552]/5 blur-3xl" />

                <div className="pointer-events-none absolute -right-40 top-1/3 h-96 w-96 rounded-full bg-[#8B0000]/5 blur-3xl" />

                <div className="relative z-10 mx-auto max-w-[1440px]">

                    {/* =================================================
              SEARCH HEADER
          ================================================= */}

                    <div className="mb-8">

                        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">

                            <div>

                                <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#8B0000] sm:text-xs">
                                    Search Results
                                </p>

                                <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-[#173F3E] sm:text-3xl">
                                    {searchData?.destination
                                        ? `Hotels in ${searchData.destination}`
                                        : "Available Hotels"}
                                </h2>

                                <div className="mt-3 h-1 w-12 rounded-full bg-[#D7A74A]" />

                                <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-500">
                                    Find comfortable stays with excellent
                                    locations, useful amenities and competitive
                                    hotel rates.
                                </p>

                            </div>

                            {/* Result Count */}

                            {!loading && !error && hotels.length > 0 && (
                                <div className="flex w-fit items-center gap-3 rounded-2xl border border-slate-100 bg-white px-5 py-3 shadow-[0_10px_35px_rgba(23,63,62,0.06)]">

                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#8B0000]/5 text-[#8B0000]">
                                        <FaHotel />
                                    </div>

                                    <div>
                                        <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                                            Hotels Found
                                        </p>

                                        <p className="text-xl font-extrabold text-[#173F3E]">
                                            {hotels.length}
                                        </p>
                                    </div>

                                </div>
                            )}

                        </div>

                        {/* Destination Search Bar */}

                        <div className="mt-7 flex flex-col gap-3 rounded-[22px] border border-slate-100 bg-white p-3 shadow-[0_10px_35px_rgba(23,63,62,0.05)] sm:flex-row sm:items-center sm:justify-between">

                            <div className="flex items-center gap-3 px-2">

                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#8B0000]/5 text-[#8B0000]">
                                    <FaMapMarkerAlt />
                                </div>

                                <div>
                                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                                        Searching Destination
                                    </p>

                                    <p className="text-sm font-bold text-[#173F3E]">
                                        {searchData?.destination || "Dubai, UAE"}
                                    </p>
                                </div>

                            </div>

                            <button
                                onClick={() => navigate("/hotels")}
                                className="
                  inline-flex items-center justify-center gap-2
                  rounded-xl border border-[#8B0000]/15
                  bg-[#8B0000]/5 px-5 py-3
                  text-xs font-bold text-[#8B0000]
                  transition-all duration-300
                  hover:border-[#8B0000]
                  hover:bg-[#8B0000]
                  hover:text-white
                "
                            >
                                <FaSearch />
                                Search Again
                            </button>

                        </div>
                    </div>

                    {/* =================================================
              LOADING
          ================================================= */}

                    {loading ? (

                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">

                            {[1, 2, 3, 4, 5, 6].map((item) => (
                                <div
                                    key={item}
                                    className="overflow-hidden rounded-[26px] border border-slate-100 bg-white shadow-[0_10px_35px_rgba(23,63,62,0.05)]"
                                >

                                    <div className="h-56 animate-pulse bg-slate-200" />

                                    <div className="p-6">

                                        <div className="h-5 w-3/4 animate-pulse rounded-lg bg-slate-200" />

                                        <div className="mt-3 h-4 w-1/2 animate-pulse rounded-lg bg-slate-200" />

                                        <div className="mt-5 h-4 w-full animate-pulse rounded-lg bg-slate-200" />

                                        <div className="mt-2 h-4 w-4/5 animate-pulse rounded-lg bg-slate-200" />

                                        <div className="mt-5 flex gap-2">
                                            <div className="h-7 w-16 animate-pulse rounded-full bg-slate-200" />
                                            <div className="h-7 w-16 animate-pulse rounded-full bg-slate-200" />
                                            <div className="h-7 w-20 animate-pulse rounded-full bg-slate-200" />
                                        </div>

                                        <div className="mt-6 h-12 w-full animate-pulse rounded-xl bg-slate-200" />

                                    </div>
                                </div>
                            ))}

                        </div>

                    ) : error ? (

                        /* =================================================
                           ERROR
                        ================================================= */

                        <div className="overflow-hidden rounded-[28px] border border-red-100 bg-white shadow-[0_15px_50px_rgba(23,63,62,0.06)]">

                            <div className="px-6 py-20 text-center sm:px-10">

                                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[22px] bg-[#8B0000]/5 text-[#8B0000]">
                                    <FaRedo className="text-2xl" />
                                </div>

                                <p className="mt-6 text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#8B0000]">
                                    Search Error
                                </p>

                                <h3 className="mt-2 text-2xl font-extrabold text-[#173F3E]">
                                    Unable to Load Hotels
                                </h3>

                                <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">
                                    Something went wrong while searching for
                                    hotels. Please try again.
                                </p>

                                <button
                                    onClick={() => window.location.reload()}
                                    className="
                    mt-7 inline-flex items-center justify-center gap-2
                    rounded-xl bg-[#8B0000] px-7 py-3
                    text-sm font-bold text-white shadow-lg
                    transition-all duration-300
                    hover:-translate-y-0.5
                    hover:bg-[#700000]
                    hover:shadow-xl
                  "
                                >
                                    <FaRedo />
                                    Try Again
                                </button>

                            </div>
                        </div>

                    ) : hotels.length > 0 ? (

                        /* =================================================
                           HOTEL GRID
                        ================================================= */

                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">

                            {hotels.map((hotel) => (

                                <div
                                    key={hotel.id}
                                    onClick={() => openHotel(hotel.slug)}
                                    className="
                    group cursor-pointer overflow-hidden
                    rounded-[26px] border border-slate-100
                    bg-white shadow-[0_10px_35px_rgba(23,63,62,0.06)]
                    transition-all duration-500
                    hover:-translate-y-2
                    hover:border-[#8B0000]/20
                    hover:shadow-[0_25px_60px_rgba(139,0,0,0.10)]
                  "
                                >

                                    {/* IMAGE */}

                                    <div
                                        className="relative h-56 w-full overflow-hidden sm:h-60"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openHotel(hotel.slug);
                                        }}
                                    >

                                        <img
                                            src={`https://uaediscountedhotels.com/uploads/hotels/${hotel.image}`}
                                            alt={hotel.name}
                                            className="
                        h-full w-full object-cover
                        transition-transform duration-700
                        group-hover:scale-110
                      "
                                        />

                                        {/* Image Overlay */}

                                        <div className="absolute inset-0 bg-gradient-to-t from-[#073F3D]/90 via-[#073F3D]/10 to-transparent" />

                                        <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/30 to-transparent" />

                                        {/* LOCATION */}

                                        <div className="absolute left-4 top-4 z-20">

                                            <span className="inline-flex max-w-[210px] items-center gap-1.5 rounded-full border border-white/30 bg-white/95 px-3 py-1.5 text-xs font-bold text-[#173F3E] shadow-lg backdrop-blur-md">

                                                <FaMapMarkerAlt className="shrink-0 text-[#8B0000]" />

                                                <span className="truncate">
                                                    {hotel.location || "Dubai, UAE"}
                                                </span>

                                            </span>

                                        </div>

                                        {/* WISHLIST */}

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleWishlist(hotel);
                                            }}
                                            className={`
                        absolute right-4 top-4 z-20
                        flex h-11 w-11 items-center justify-center
                        rounded-full border border-white/40
                        bg-white/95 shadow-lg backdrop-blur-md
                        transition-all duration-300 hover:scale-110
                        ${isWishlisted(hotel.id)
                                                    ? "text-[#8B0000]"
                                                    : "text-slate-500 hover:bg-[#8B0000]/5 hover:text-[#8B0000]"
                                                }
                      `}
                                            aria-label={
                                                isWishlisted(hotel.id)
                                                    ? "Remove from wishlist"
                                                    : "Add to wishlist"
                                            }
                                        >
                                            {isWishlisted(hotel.id) ? (
                                                <FaHeart className="text-base" />
                                            ) : (
                                                <FaRegHeart className="text-base" />
                                            )}
                                        </button>

                                        {/* RATING */}

                                        <div className="absolute bottom-4 left-4 z-20">

                                            <div className="flex items-center gap-2 rounded-xl border border-white/30 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-md">

                                                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#D7A74A] text-[#173F3E]">
                                                    <FaStar className="text-[10px]" />
                                                </div>

                                                <div>

                                                    <div className="flex items-center gap-1">
                                                        <span className="text-sm font-extrabold text-[#173F3E]">
                                                            {hotel.rating || "4.5"}
                                                        </span>

                                                        <span className="text-[10px] font-semibold text-slate-400">
                                                            / 5
                                                        </span>
                                                    </div>

                                                    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                                                        Excellent
                                                    </p>

                                                </div>
                                            </div>
                                        </div>

                                        {/* PRICE */}

                                        <div className="absolute bottom-4 right-4 z-20 rounded-2xl bg-[#8B0000] px-4 py-2.5 text-white shadow-xl">

                                            <p className="text-[9px] font-semibold uppercase tracking-wider text-white/65">
                                                From
                                            </p>

                                            <p className="mt-0.5 text-sm font-extrabold">
                                                AED {Math.round(hotel.price_per_night)}

                                                <span className="ml-1 text-[10px] font-medium text-white/65">
                                                    / night
                                                </span>
                                            </p>

                                        </div>

                                    </div>

                                    {/* CONTENT */}

                                    <div className="p-5 sm:p-6">

                                        {/* Hotel Name */}

                                        <h3
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openHotel(hotel.slug);
                                            }}
                                            className="
                        line-clamp-1 text-lg font-extrabold
                        text-[#173F3E] transition-colors
                        duration-300 group-hover:text-[#8B0000]
                        sm:text-xl
                      "
                                        >
                                            {hotel.name}
                                        </h3>

                                        {/* Location */}

                                        <div className="mt-2 flex items-center gap-2 text-slate-500">

                                            <FaMapMarkerAlt className="shrink-0 text-sm text-[#8B0000]" />

                                            <span className="line-clamp-1 text-sm">
                                                {hotel.location || "Dubai, UAE"}
                                            </span>

                                        </div>

                                        <div className="my-4 h-px bg-slate-100" />

                                        {/* Description */}

                                        <p className="min-h-[48px] line-clamp-2 text-sm leading-6 text-slate-500">
                                            {hotel.short_description?.replace(/<[^>]*>/g, "")}
                                        </p>

                                        {/* AMENITIES */}

                                        <div className="mt-5 flex flex-wrap gap-2">

                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#8B0000]/10 bg-[#8B0000]/5 px-3 py-1.5 text-[10px] font-bold text-[#8B0000]">
                                                <FaSwimmingPool />
                                                Pool
                                            </span>

                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#0B5552]/10 bg-[#0B5552]/5 px-3 py-1.5 text-[10px] font-bold text-[#0B5552]">
                                                <FaWifi />
                                                WiFi
                                            </span>

                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#D7A74A]/20 bg-[#D7A74A]/10 px-3 py-1.5 text-[10px] font-bold text-[#8A671C]">
                                                <FaUtensils />
                                                Restaurant
                                            </span>

                                        </div>

                                        {/* BUTTON */}

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openHotel(hotel.slug);
                                            }}
                                            className="
                        mt-6 flex w-full items-center justify-center
                        gap-2 rounded-xl bg-[#8B0000] px-5 py-3.5
                        text-sm font-extrabold text-white shadow-md
                        transition-all duration-300
                        hover:-translate-y-0.5
                        hover:bg-[#700000]
                        hover:shadow-xl
                      "
                                        >
                                            View Hotel Details

                                            <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
                                        </button>

                                    </div>
                                </div>
                            ))}

                        </div>

                    ) : (

                        /* =================================================
                           EMPTY STATE
                        ================================================= */

                        <div className="overflow-hidden rounded-[28px] border border-slate-100 bg-white shadow-[0_15px_50px_rgba(23,63,62,0.06)]">

                            <div className="px-6 py-20 text-center sm:px-10">

                                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[22px] bg-[#8B0000]/5 text-[#8B0000]">
                                    <FaHotel className="text-2xl" />
                                </div>

                                <p className="mt-6 text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#8B0000]">
                                    No Results
                                </p>

                                <h3 className="mt-2 text-2xl font-extrabold text-[#173F3E]">
                                    No Hotels Found
                                </h3>

                                <div className="mx-auto mt-4 h-1 w-12 rounded-full bg-[#D7A74A]" />

                                <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">
                                    We couldn't find any hotels matching
                                    your search.

                                    <br className="hidden sm:block" />

                                    Try another destination or explore all
                                    available hotels.
                                </p>

                                <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">

                                    <button
                                        onClick={() => navigate("/hotels")}
                                        className="
                      inline-flex w-full items-center justify-center
                      gap-2 rounded-xl bg-[#8B0000] px-7 py-3.5
                      text-sm font-bold text-white shadow-lg
                      transition-all duration-300
                      hover:-translate-y-0.5 hover:bg-[#700000]
                      hover:shadow-xl sm:w-auto
                    "
                                    >
                                        Explore All Hotels
                                        <FaArrowRight className="text-xs" />
                                    </button>

                                    <button
                                        onClick={() => navigate("/")}
                                        className="
                      inline-flex w-full items-center justify-center
                      rounded-xl border border-slate-200 bg-white
                      px-7 py-3.5 text-sm font-bold text-[#173F3E]
                      transition-all duration-300
                      hover:border-[#8B0000]/30
                      hover:bg-[#8B0000]/5
                      hover:text-[#8B0000] sm:w-auto
                    "
                                    >
                                        Back to Home
                                    </button>

                                </div>
                            </div>
                        </div>
                    )}

                    {/* =================================================
              TRUST / SECURITY STRIP
          ================================================= */}

                    {!loading && !error && hotels.length > 0 && (

                        <div className="mt-8 grid gap-4 sm:grid-cols-3">

                            <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_8px_25px_rgba(23,63,62,0.04)]">

                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#8B0000]/5 text-[#8B0000]">
                                    <FaShieldAlt />
                                </div>

                                <div>
                                    <p className="text-xs font-extrabold text-[#173F3E]">
                                        Trusted Stays
                                    </p>

                                    <p className="mt-0.5 text-[10px] text-slate-400">
                                        Carefully selected hotels
                                    </p>
                                </div>

                            </div>

                            <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_8px_25px_rgba(23,63,62,0.04)]">

                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#D7A74A]/15 text-[#9A741F]">
                                    <FaStar />
                                </div>

                                <div>
                                    <p className="text-xs font-extrabold text-[#173F3E]">
                                        Great Locations
                                    </p>

                                    <p className="mt-0.5 text-[10px] text-slate-400">
                                        Convenient places to stay
                                    </p>
                                </div>

                            </div>

                            <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_8px_25px_rgba(23,63,62,0.04)]">

                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0B5552]/5 text-[#0B5552]">
                                    <FaHotel />
                                </div>

                                <div>
                                    <p className="text-xs font-extrabold text-[#173F3E]">
                                        Easy Booking
                                    </p>

                                    <p className="mt-0.5 text-[10px] text-slate-400">
                                        Simple hotel reservations
                                    </p>
                                </div>

                            </div>

                        </div>
                    )}

                </div>
            </section>
        </div>
    );
}